import { useEffect, useState } from 'react';

const emptySnapshot = { identity: null, data: null, loading: true, refreshing: false, error: '', updatedAt: null };

const useApplicationUpdates = (load, { enabled = true, poll = true, identity }) => {
  const [snapshot, setSnapshot] = useState(emptySnapshot);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    let inFlight = false;
    let controller;

    const refresh = async () => {
      if (inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      controller = new AbortController();
      setSnapshot(previous => {
        const current = previous.identity === identity ? previous : emptySnapshot;
        return { ...current, identity, loading: current.data === null, refreshing: true };
      });
      try {
        const response = await load({ signal: controller.signal });
        if (!response.success || response.data == null) throw new Error('Application status could not be refreshed.');
        if (active) setSnapshot({ identity, data: response.data, loading: false, refreshing: false, error: '', updatedAt: new Date() });
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        const denied = error.response?.status === 401 || error.response?.status === 403;
        const missing = error.response?.status === 404;
        setSnapshot(previous => ({
          ...(previous.identity === identity ? previous : emptySnapshot), identity,
          ...(denied || missing ? { data: null } : {}), loading: false, refreshing: false,
          error: denied ? 'Please sign in again to view your application status.' : missing ? 'This application is no longer available.' : 'Unable to refresh application status. We will try again shortly.'
        }));
      } finally {
        inFlight = false;
      }
    };

    refresh();
    const interval = poll ? window.setInterval(refresh, 30000) : null;
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    // The first fetch can be deferred when the account opens in a background tab.
    document.addEventListener('visibilitychange', onVisible);
    if (poll) window.addEventListener('focus', refresh);
    return () => {
      active = false;
      controller?.abort();
      if (interval !== null) window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
    };
  }, [load, enabled, poll, identity]);

  if (!enabled || snapshot.identity !== identity) return { ...emptySnapshot, loading: enabled };
  return snapshot;
};

export default useApplicationUpdates;
